import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AvatarRound, BtnV2, Icon, Loader, l, useReferredValue } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  SubmitRef,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { getInstagramChannel } from 'common/core/selectors/channelSelectors'
import { HookFormTextArea } from 'components/HookFormControls/HookFormTextArea/HookFormTextArea'
import { HookFormTextInput } from 'components/HookFormControls/HookFormTextInput/HookFormTextInput'
import { GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'
import { useUpdateOnlyEffect } from 'utils/commonHooks'

import sharedCm from '../../../EditorLayout/styles.module.css'
import { ContextEditorFormValues, MessageEditorFormValues } from '../../lib/types'

import { useContentGenerator } from './hooks/useContentGenerator'

import cm from './styles.module.css'

interface MessageEditorProps {
  contextData: ContextEditorFormValues | null
  contentData: MessageEditorFormValues | null
  isActive: boolean
  isPublishBlocked: boolean
  onFinish: (data: MessageEditorFormValues) => void
}

export const MessageEditor = forwardRef<SubmitRef<MessageEditorFormValues>, MessageEditorProps>(
  ({ contextData, contentData, isActive, isPublishBlocked, onFinish }, ref) => {
    const dispatch = useAppDispatch()

    const { avatar } = useAppSelector(getInstagramChannel)

    const { goalId } = useGoalEditorContext(GoalType.LEAD_GEN)

    const { formState, handleSubmit, reset, setValue, control } = useForm({
      defaultValues: {
        message: contentData?.message ?? '',
        buttonTitle: contentData?.buttonTitle ?? '',
      },
      resolver: zodResolver(
        z.object({
          message: z
            .string()
            .min(1, l.translate('Enter message to continue'))
            .max(640, l.translate('Message must be {count} characters or fewer', { count: 640 })),
          buttonTitle: z
            .string()
            .min(1, l.translate('Enter button title to continue'))
            .max(
              20,
              l.translate('Button title must be {count} characters or fewer', { count: 20 }),
            ),
        }),
      ),
    })

    const goalIdRef = useReferredValue(goalId)
    const urlRef = useReferredValue(contextData?.url)

    const onGenerate = useCallback(
      ({ message, buttonTitle }: { message: string; buttonTitle: string }) => {
        setValue('message', message)
        setValue('buttonTitle', buttonTitle)
      },
      [setValue],
    )

    const { generateContent, isLoading } = useContentGenerator({
      onGenerate,
    })

    const onSubmit = useCallback(
      (data: MessageEditorFormValues) => {
        if (isLoading) {
          return
        }

        reset(data)

        onFinish(data)
      },
      [isLoading, onFinish, reset],
    )

    const context = contextData?.context

    const runGeneration = useCallback(() => {
      if (context && urlRef.current) {
        generateContent({ context, url: urlRef.current, goalId: goalIdRef.current })
      }
    }, [context, generateContent, goalIdRef, urlRef])

    useUpdateOnlyEffect(runGeneration, [context, generateContent])

    useEffect(() => {
      if (isActive) {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: formState.isDirty }))
      } else {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
      }
    }, [dispatch, formState.isDirty, isActive])

    const formId = useId()

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          let result: SubmitResult<MessageEditorFormValues> = { isValid: false }

          await handleSubmit((data) => {
            result = { isValid: true, data }
            reset(data)
          })()
          return result
        },
      }),
      [handleSubmit, reset],
    )

    return (
      <section>
        <div className={sharedCm.section}>
          <h4 className={cx('font-medium', !contextData && 'text-secondary')}>
            {l.translate('Edit the message AI wrote for you')}
          </h4>

          {contextData && (
            <div
              className="p-relative"
              // @ts-expect-error type for inert is supported starting from React 19+
              // It's also not working for now when we pass true/false, that's why there is an empty string and undefined values
              inert={isLoading ? '' : undefined}
            >
              <p className="m-t-xxs m-b-md text-secondary">
                {l.translate('Your edits will improve AI over time')}

                <Icon.Question
                  data-title={l.translate(
                    'AI gets smarter from your edits, capturing your style to write replies that match your tone more effectively.',
                  )}
                  size={16}
                  data-title-at="left"
                  className={cm.questionIcon}
                />
              </p>

              <form id={formId} onSubmit={handleSubmit(onSubmit)}>
                <HookFormTextArea
                  className={cm.textarea}
                  ariaLabel={l.translate('Message')}
                  minRows={2}
                  maxRows={6}
                  disabled={!isActive}
                  control={control}
                  name="message"
                />

                <div className={cm.buttonInputWrapper}>
                  <div className={cm.buttonInput__tooltipWrapper}>
                    <HookFormTextInput
                      control={control}
                      name="buttonTitle"
                      ariaLabel={l.translate('Button title')}
                      helpText={l.translate('This link will appear as a button in the DM')}
                      disabled={!isActive}
                    />
                    <Icon.Link
                      aria-hidden
                      data-title={urlRef.current}
                      data-title-at="top"
                      className={cm.buttonInput__tooltip}
                      size={16}
                    />
                  </div>
                  <AvatarRound aria-hidden className={cm.avatar} src={avatar} />
                </div>
              </form>

              {isActive && (
                <BtnV2
                  onClick={runGeneration}
                  className="m-t-md"
                  leftIcon="MagicWand"
                  variant="ghost-primary"
                >
                  {l.translate('Try another message')}
                </BtnV2>
              )}

              {isLoading && (
                <div className={cm.loader}>
                  <Loader className={cm.loader__spinner} size={16} />

                  <p className="text-secondary">
                    {l.translate('AI is writing based on your input…')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {isActive && !contentData && (
          <div className="m-t-sm d-flex justify-end">
            <BtnV2 disabled={isLoading} form={formId} type="submit">
              {isPublishBlocked ? l.translate('Save') : l.translate('Next')}
            </BtnV2>
          </div>
        )}
      </section>
    )
  },
)
